import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { SurveyContext } from "../context/SurveyContext";
import { useNotificationSocket } from "../context/SocketContext";
import MentionInput from "./MentionInput";
import api from "../services/api";
import { getProfilePicture } from "../utils/avatar";
import { FaBell, FaSpinner, FaRegComment, FaHeart, FaRetweet, FaUserPlus, FaAt } from "react-icons/fa";

const TABS = [
  { key: "replies", label: "Replies to you" },
  { key: "mentions", label: "Mentions" },
  { key: "all", label: "All" },
];

const typeIcon = {
  comment: { icon: FaRegComment, color: "text-spiders-success" },
  reply: { icon: FaRegComment, color: "text-spiders-success" },
  thread_activity: { icon: FaRegComment, color: "text-spiders-success" },
  like: { icon: FaHeart, color: "text-spiders-error" },
  repost: { icon: FaRetweet, color: "text-spiders-accent-400" },
  follow: { icon: FaUserPlus, color: "text-spiders-primary-400" },
  mention: { icon: FaAt, color: "text-spiders-warning" },
};

const typeVerb = {
  comment: "commented on your post",
  like: "liked your post",
  repost: "reposted your post",
  follow: "started following you",
  mention: "mentioned you",
  reply: "replied to your comment",
  thread_activity: "commented in a thread you participated in",
};

const NotificationsPage = () => {
  const { user } = useContext(AuthContext);
  const { condition } = useContext(SurveyContext);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("replies");
  const [expandedReply, setExpandedReply] = useState(null); // notification _id
  const [replyContent, setReplyContent] = useState("");
  const [replySubmitting, setReplySubmitting] = useState(false);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const { data } = await api.get("/notifications", {
          params: { condition, limit: 50 },
        });
        setItems(data);
      } catch (e) {
        console.error("Failed to load notifications:", e);
      } finally {
        setLoading(false);
      }
    };
    if (user && condition) {
      fetchNotifications();
    }
  }, [user, condition]);

  // Real-time: add new notifications as they arrive
  useNotificationSocket(
    useCallback((notification) => {
      if (notification.actorId !== user?.userId) {
        setItems((prev) => [notification, ...prev]);
      }
    }, [user?.userId])
  );

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all", { condition });
      setItems((prev) =>
        prev.map((n) => ({ ...n, readAt: new Date().toISOString() }))
      );
    } catch (e) {
      console.error("Failed to mark all read:", e);
    }
  };

  const handleNotificationClick = (n) => {
    if (n.targetType === "message") {
      const commentParam = n.sourceCommentId ? `&commentId=${n.sourceCommentId}` : '';
      navigate(`/messages/${n.targetId}?condition=${condition}${commentParam}`);
    } else if (n.targetType === "user") {
      navigate(
        `/profile/${n.actorUsername || n.actorId}?condition=${condition}`
      );
    }
  };

  const handleInlineReply = async (notification) => {
    if (!replyContent.trim() || replySubmitting) return;
    setReplySubmitting(true);
    try {
      await api.post("/comments", {
        messageId: notification.targetId,
        content: replyContent.trim(),
        condition,
      });
      setReplyContent("");
      setExpandedReply(null);
    } catch (error) {
      console.error("Error sending reply:", error);
    } finally {
      setReplySubmitting(false);
    }
  };

  // Filter notifications by tab
  const filteredItems = useMemo(() => {
    switch (activeTab) {
      case "replies":
        return items.filter((n) =>
          ["comment", "reply"].includes(n.type)
        );
      case "mentions":
        return items.filter((n) => n.type === "mention");
      case "all":
      default:
        return items;
    }
  }, [items, activeTab]);

  // Group notifications by targetId (thread grouping)
  const groupedItems = useMemo(() => {
    const groups = [];
    const groupMap = new Map(); // targetId -> group index

    filteredItems.forEach((n) => {
      const key = n.targetId || n._id;
      if (n.targetType === "message" && groupMap.has(key)) {
        groups[groupMap.get(key)].notifications.push(n);
      } else {
        groupMap.set(key, groups.length);
        groups.push({
          targetId: key,
          targetType: n.targetType,
          notifications: [n],
        });
      }
    });

    return groups;
  }, [filteredItems]);

  if (loading) {
    return (
      <div className="min-h-screen bg-spiders-bg flex items-center justify-center">
        <div className="flex items-center space-x-3 text-spiders-text-muted">
          <FaSpinner className="animate-spin text-xl" />
          <span>Loading notifications...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-spiders-bg">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-spiders-border bg-spiders-bg-surface sticky top-0 lg:top-0 z-10">
          <h1 className="font-bold text-xl text-spiders-text-primary">
            Notifications
          </h1>
          <button
            className="text-sm text-spiders-primary-400 hover:text-spiders-primary-300 font-medium transition-colors"
            onClick={markAllRead}
          >
            Mark all as read
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex border-b border-spiders-border bg-spiders-bg-surface">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-3 text-sm font-medium text-center transition-colors relative ${
                activeTab === tab.key
                  ? "text-spiders-text-primary"
                  : "text-spiders-text-muted hover:text-spiders-text-secondary hover:bg-spiders-bg-elevated/30"
              }`}
            >
              {tab.label}
              {activeTab === tab.key && (
                <div className="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-spiders-primary-400 rounded-full" />
              )}
            </button>
          ))}
        </div>

        {/* Notification List */}
        <div className="bg-spiders-bg-surface">
          {groupedItems.length === 0 ? (
            <div className="text-spiders-text-muted text-center py-16">
              <FaBell className="w-10 h-10 mx-auto mb-3 text-spiders-border" />
              <p className="text-lg">No notifications yet</p>
              <p className="text-sm mt-1">
                When someone interacts with your posts, you'll see it here.
              </p>
            </div>
          ) : (
            groupedItems.map((group, groupIndex) => {
              const isGrouped = group.notifications.length > 1;

              return (
                <div
                  key={group.targetId + groupIndex}
                  className={`border-b border-spiders-border ${
                    isGrouped ? "bg-spiders-bg-surface" : ""
                  }`}
                >
                  {group.notifications.map((n, i) => {
                    const config = typeIcon[n.type] || typeIcon.comment;
                    const Icon = config.icon;
                    const canReply =
                      n.targetType === "message" &&
                      ["comment", "reply", "mention", "thread_activity"].includes(n.type);

                    return (
                      <div key={n._id}>
                        <button
                          className={`w-full text-left p-4 hover:bg-spiders-bg-elevated transition-colors ${
                            !n.readAt ? "bg-spiders-primary-950/30" : ""
                          } ${isGrouped && i > 0 ? "pl-8" : ""}`}
                          onClick={() => handleNotificationClick(n)}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-1">
                              <img
                                src={getProfilePicture(n.actorUsername || "unknown", n.actorAvatarSeed)}
                                alt={n.actorUsername}
                                className="w-8 h-8 rounded-full border border-spiders-border"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-1.5 text-sm text-spiders-text-primary">
                                <Icon className={`w-3.5 h-3.5 ${config.color} flex-shrink-0`} />
                                <span>
                                  <span className="font-bold text-spiders-primary-400">
                                    @{n.actorUsername || "Someone"}
                                  </span>{" "}
                                  {typeVerb[n.type] || "interacted"}
                                </span>
                              </div>
                              {n.contentPreview && (
                                <p className="text-xs text-spiders-text-secondary mt-1 line-clamp-2 italic">
                                  "{n.contentPreview}"
                                </p>
                              )}
                              <div className="text-xs text-spiders-text-muted mt-1 flex items-center space-x-2">
                                <span>{new Date(n.createdAt).toLocaleString()}</span>
                                {!n.readAt && (
                                  <span className="spiders-badge-accent text-xs">
                                    New
                                  </span>
                                )}
                              </div>

                              {/* Inline reply toggle */}
                              {canReply && expandedReply !== n._id && (
                                <button
                                  className="mt-1.5 text-xs text-spiders-primary-400 hover:text-spiders-primary-300 transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedReply(n._id);
                                    setReplyContent("");
                                  }}
                                >
                                  Quick reply...
                                </button>
                              )}
                            </div>
                          </div>
                        </button>

                        {/* Expanded inline reply */}
                        {expandedReply === n._id && (
                          <div className="px-4 pb-4 pl-16">
                            <MentionInput
                              value={replyContent}
                              onChange={setReplyContent}
                              placeholder={`Reply to @${n.actorUsername}...`}
                              rows={2}
                              className="text-sm"
                              mentionableUsers={
                                n.actorUsername
                                  ? [{ username: n.actorUsername, _id: n.actorId }]
                                  : []
                              }
                            />
                            <div className="flex justify-end items-center space-x-2 mt-2">
                              <button
                                className="text-spiders-text-muted hover:text-spiders-text-secondary text-xs px-2 py-1"
                                onClick={() => setExpandedReply(null)}
                              >
                                Cancel
                              </button>
                              <button
                                className="spiders-btn-primary text-xs py-1 px-3"
                                onClick={() => handleInlineReply(n)}
                                disabled={!replyContent.trim() || replySubmitting}
                              >
                                {replySubmitting ? (
                                  <FaSpinner className="animate-spin w-3 h-3" />
                                ) : (
                                  "Reply"
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
